import { formatDateTime } from '@/client/utils/datetime';

export const STATUS_STYLES: Record<string, { badge: string; dot: string }> = {
  LIVE: { badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20', dot: 'bg-emerald-500' },
  REVIEW: { badge: 'bg-amber-50 text-amber-700 ring-amber-600/20', dot: 'bg-amber-500' },
  REJECTED: { badge: 'bg-rose-50 text-rose-700 ring-rose-600/20', dot: 'bg-rose-500' },
  SUSPENDED: { badge: 'bg-orange-50 text-orange-700 ring-orange-600/20', dot: 'bg-orange-500' },
  DRAFT: { badge: 'bg-gray-100 text-gray-600 ring-gray-500/20', dot: 'bg-gray-400' },
  INACTIVE: { badge: 'bg-gray-100 text-gray-600 ring-gray-500/20', dot: 'bg-gray-400' },
  ARCHIVED: { badge: 'bg-gray-100 text-gray-600 ring-gray-500/20', dot: 'bg-gray-400' },
  DELETED: { badge: 'bg-red-100 text-red-700 ring-red-600/20', dot: 'bg-red-500' },
};

export const truthy = (v: unknown) => v === 1 || v === true || v === '1';

/**
 * Several sub-resource endpoints return an error object (e.g.
 * `{ error: "eid_not_found" }`) instead of an empty list when there's no data,
 * so guard every list render against non-arrays before mapping.
 */
export const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

// Timezone-aware + labelled, so a listing's timestamps read unambiguously
// regardless of the viewer's locale (see utils/datetime).
export const fmtDate = (v: unknown) => formatDateTime(v);

export const money = (n: unknown) =>
  typeof n === 'number' ? `GHS ${n.toLocaleString()}` : '—';

/**
 * `entity_attribute` rows carry the value in `content` (with numeric/alt/json
 * variants). The readable name comes from the joined `attribute_type`
 * (`name_friendly` preferred, falling back to its internal `name`); older
 * payloads without the join fall back to `type_other`/eatid.
 */
export const attrLabel = (a: any): string =>
  a.attribute_type?.name_friendly ||
  a.attribute_type?.name ||
  a.name_friendly ||
  a.name ||
  a.type_other ||
  `Attribute #${a.eatid ?? '—'}`;

export const attrValue = (a: any): string => {
  const v = a.content ?? a.content_numeric ?? a.content_alt;
  if (v !== undefined && v !== null && String(v).trim() !== '') return String(v);
  if (a.content_json != null && a.content_json !== '') {
    try {
      return typeof a.content_json === 'string'
        ? a.content_json
        : JSON.stringify(a.content_json);
    } catch {
      /* fall through */
    }
  }
  return '—';
};

export const ownerInitials = (o: { first_name?: string; last_name?: string }) =>
  [o.first_name, o.last_name].filter(Boolean).map((n) => n![0].toUpperCase()).join('') || '?';
